import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Label,
  List,
  Modal,
  Segment,
} from 'semantic-ui-react';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import ModalTierPricingSchedule from '../containers/modals/TierPricingSchedule';

const DragHandle = SortableHandle(() => <Icon size="large" disabled name="content" />);

const SortableItem = SortableElement(({ data, idx, onChange, onDeleteItem, onSchedule }) => {
  const inputDateFormat = 'DD/MM/YYYY, hh:mm A';
  let scheduleColor = null;
  let scheduleIcon = 'calendar outline';
  let scheduleTitle = 'Set schedule';
  if (data.scheduleStartAt && data.scheduleEndAt) {
    if (
      moment(data.scheduleStartAt).unix() < moment().unix() &&
      moment(data.scheduleEndAt).unix() > moment().unix()
      ) {
      scheduleIcon = 'calendar check';
      scheduleColor = 'green';
      scheduleTitle = `Active schedule: ${moment(data.scheduleStartAt).format(inputDateFormat)} - ${moment(data.scheduleEndAt).format(inputDateFormat)}`;
    } else if (moment(data.scheduleStartAt).unix() > moment().unix()) {
      scheduleIcon = 'calendar check';
      scheduleColor = 'teal';
      scheduleTitle = `Future schedule: ${moment(data.scheduleStartAt).format(inputDateFormat)} - ${moment(data.scheduleEndAt).format(inputDateFormat)}`;
    } else {
      scheduleIcon = 'calendar times';
      scheduleColor = 'red';
      scheduleTitle = 'Schedule ended';
    }
  } else if (data.scheduleStartAt) {
    if (moment(data.scheduleStartAt).unix() < moment().unix()) {
      scheduleIcon = 'calendar check';
      scheduleColor = 'green';
      scheduleTitle = `Active schedule: ${moment(data.scheduleStartAt).format(inputDateFormat)} - n/a`;
    } else {
      scheduleIcon = 'calendar check';
      scheduleColor = 'teal';
      scheduleTitle = `Future schedule: ${moment(data.scheduleStartAt).format(inputDateFormat)} - n/a`;
    }
  } else if (data.scheduleEndAt) {
    if (moment(data.scheduleEndAt).unix() > moment().unix()) {
      scheduleIcon = 'calendar check';
      scheduleColor = 'green';
      scheduleTitle = `Active schedule: n/a - ${moment(data.scheduleEndAt).format(inputDateFormat)}`;
    } else {
      scheduleIcon = 'calendar times';
      scheduleColor = 'red';
      scheduleTitle = 'Schedule ended';
    }
  }
  return (
    <Grid className="item">
      <Grid.Row verticalAlign="middle">
        <Grid.Column mobile={3} tablet={1} computer={1}>
          <DragHandle />
        </Grid.Column>
        <Grid.Column mobile={10} tablet={13} computer={14}>
          <Grid>
            <Grid.Row className="compact">
              <Grid.Column mobile={16} tablet={16} computer={5}>
                <Grid>
                  <Grid.Row verticalAlign="middle" className="compact">
                    <Grid.Column width={12}>
                      <Input
                        fluid
                        name="name"
                        onChange={(e, d) => onChange(e, d, idx)}
                        placeholder="Name*"
                        value={data.name}
                      />
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Button circular size="small" icon color={scheduleColor} title={scheduleTitle} onClick={e => onSchedule(e, data, idx)}>
                        <Icon name={scheduleIcon} fitted />
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
              <Grid.Column mobile={16} tablet={16} computer={5}>
                <Input
                  fluid
                  name="description"
                  onChange={(e, d) => onChange(e, d, idx)}
                  placeholder="Description"
                  value={data.description}
                />
              </Grid.Column>
              <Grid.Column mobile={16} tablet={16} computer={6}>
                <Input
                  fluid
                  name="formulaPrice"
                  onChange={(e, d) => onChange(e, d, idx)}
                  placeholder="Nett Price / Formula*"
                  value={data.formulaPrice}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column mobile={3} tablet={2} computer={1} className="colRight">
          <Button
            onClick={e => onDeleteItem(e, idx)}
            negative
            icon="trash"
            compact
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
});

const SortableList = SortableContainer(({
  addButtonIsDisabled,
  helpModal,
  items,
  onAdd,
  onChange,
  onDeleteItem,
  onSchedule,
}) => (
  <Segment color="teal">
    <Grid className="head">
      <Grid.Row verticalAlign="middle">
        <Grid.Column textAlign="center">
          <h3>Discount Package Setup <Button onClick={helpModal} circular icon="help" size="mini" /></h3>
        </Grid.Column>
      </Grid.Row>
    </Grid>
    <Grid className="item mobile-hide">
      <Grid.Row verticalAlign="middle">
        <Grid.Column width={1} />
        <Grid.Column width={14}>
          <Grid>
            <Grid.Row className="compact">
              <Grid.Column width={5}textAlign="center">
                <Grid.Row className="compact">
                  <Grid.Column width={12}>
                    <b>Name*</b>
                  </Grid.Column>
                  <Grid.Column width={4} />
                </Grid.Row>
              </Grid.Column>
              <Grid.Column width={5} textAlign="center">
                <b>Description</b>
              </Grid.Column>
              <Grid.Column width={6} textAlign="center">
                <b>Nett Price / Formula*</b>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column width={1} />
      </Grid.Row>
    </Grid>
    {items.map((data, index) => (
      <SortableItem
        data={data}
        idx={index}
        index={index}
        key={`item-${index}`}
        onChange={onChange}
        onDeleteItem={onDeleteItem}
        onSchedule={onSchedule}
      />
    ))}
    <Grid className="foot">
      <Grid.Row verticalAlign="middle">
        <Grid.Column textAlign="center">
          <Button
            fluid
            onClick={onAdd}
            disabled={addButtonIsDisabled()}
          >
            Add Discount Package
          </Button>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </Segment>
));

export default class ProjectPricingTier extends Component {
  static propTypes = {
    data: PropTypes.oneOfType([PropTypes.array]).isRequired,
    onDelete: PropTypes.func.isRequired,
    onChangeData: PropTypes.func.isRequired,
    onSaveData: PropTypes.func.isRequired,
    onDefaultInputChange: PropTypes.func.isRequired,
    defaultBumiProjectPricingID: PropTypes.string.isRequired,
    defaultProjectPricingID: PropTypes.string.isRequired,
  }
  static defaultValues = {
    id: '',
    position: 1,
    name: '',
    description: '',
    formulaPrice: '',
  };
  static getPropsToState = (props) => {
    const data = props.data || [];
    return {
      data,
    };
  }
  constructor(props) {
    super(props);
    const state = ProjectPricingTier.getPropsToState(props);
    this.state = {
      ...state,
      deleteModalIsOpen: false,
      deleteItemName: '',
      deleteItemIndex: null,
      helpModalIsOpen: false,
      scheduleModalIsOpen: false,
      scheduleData: null,
      scheduleIndex: null,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = ProjectPricingTier.getPropsToState(nextProps);
    if (JSON.stringify(this.state.data) !== JSON.stringify(state.data)) {
      this.setState({ ...state });
    }
  }
  onSortEnd = ({ oldIndex, newIndex }) => {
    this.sortDataByIndex(arrayMove(this.state.data, oldIndex, newIndex));
  };
  getDefaultOptions = () => {
    const data = this.props.data.filter(d => d.id !== '').sort((a, b) => a.position - b.position);
    const options = [{
      text: 'Disabled',
      value: '',
    }];
    data.forEach(d => options.push({
      text: d.name,
      value: d.id,
    }));
    return options;
  }
  getSortedData = data => data.sort((a, b) => a.position - b.position);
  sortDataByIndex = (newData) => {
    const data = newData;
    data.forEach((d, index) => {
      data[index].position = index + 1;
    });
    this.setState({ data }, () => this.props.onChangeData(this.state.data));
  }
  handleOnChange = (e, data, index) => {
    const newData = this.state.data;
    newData[index][data.name] = data.value;
    this.setState({ data: newData }, () => this.props.onChangeData(this.state.data));
  }
  handleOnAdd = (e) => {
    e.preventDefault();
    const data = this.state.data;
    const lastID = data.length;
    const newItem = Object.assign({}, ProjectPricingTier.defaultValues);
    if (lastID > 0) {
      newItem.position = data[lastID - 1].position;
    }
    data.push(newItem);
    this.sortDataByIndex(data);
  }
  handleOnDelete = (e, index) => {
    e.preventDefault();
    const data = this.state.data;
    this.setState({
      deleteModalIsOpen: true,
      deleteItemName: data[index].name,
      deleteItemIndex: index,
    });
  }
  handleDeleteModalConfirm = () => {
    const data = this.state.data;
    const deleteID = data[this.state.deleteItemIndex].id;
    if (deleteID !== '') {
      this.props.onDelete(deleteID);
    } else {
      data.splice(this.state.deleteItemIndex, 1);
      this.sortDataByIndex(data);
    }
    this.setState({
      deleteModalIsOpen: false,
      deleteItemName: '',
      deleteItemIndex: null,
    });
  }
  handleDeleteModalCancel = () => {
    this.setState({
      deleteModalIsOpen: false,
      deleteItemName: '',
      deleteItemIndex: null,
    });
  }
  handleHelpModal = (e) => {
    e.preventDefault();
    this.setState({
      helpModalIsOpen: true,
    });
  }
  handleHelpModalClose = () => {
    this.setState({
      helpModalIsOpen: false,
    });
  }
  handleOnSchedule = (e, data, idx) => {
    e.preventDefault();
    this.setState({
      scheduleModalIsOpen: true,
      scheduleData: data,
      scheduleIndex: idx,
    });
  }
  handleOnScheduleConfirm = (e, newData) => {
    const { data, scheduleIndex } = this.state;
    data[scheduleIndex] = newData;
    this.setState({
      scheduleModalIsOpen: false,
      scheduleData: null,
      scheduleIndex: null,
    }, () => {
      this.props.onSaveData(data);
    });
  }
  handleOnScheduleClose = () => {
    this.setState({
      scheduleModalIsOpen: false,
      scheduleData: null,
      scheduleIndex: null,
    });
  }
  checkAddButtonDisabled = () => {
    const blankCount = this.state.data.filter(d => d.name === '' || d.formulaPrice === '').length;
    if (blankCount > 0) {
      return true;
    }
    return false;
  }
  render() {
    const {
      data,
      deleteItemName,
      deleteModalIsOpen,
      helpModalIsOpen,
      scheduleModalIsOpen,
      scheduleData,
    } = this.state;
    const {
      defaultBumiProjectPricingID,
      defaultProjectPricingID,
      onDefaultInputChange,
    } = this.props;
    return (
      <Grid className="projectPricingTier">
        <Grid.Row>
          <Grid.Column width={16}>
            <ModalTierPricingSchedule
              data={scheduleData}
              onClose={this.handleOnScheduleClose}
              onConfirm={this.handleOnScheduleConfirm}
              open={scheduleModalIsOpen}
            />
            <Modal open={helpModalIsOpen} size="small">
              <Header icon="help" content="Formula Help" />
              <Modal.Content>
                <b>Formula Operators & Syntax</b><br />
                <List>
                  <List.Item><Label size="small">+</Label> Addition.<br /></List.Item>
                  <List.Item><Label size="small">-</Label> Subtraction.<br /></List.Item>
                  <List.Item><Label size="small">*</Label> Multiplication.<br /></List.Item>
                  <List.Item><Label size="small">/</Label> Division.<br /></List.Item>
                  <List.Item><Label size="small">.</Label> Decimal point for cents.<br /></List.Item>
                  <List.Item><Label size="small">(</Label> <Label size="small">)</Label> Parenthesis to control order of evaluation.<br /></List.Item>
                  <List.Item><Label size="small">SPA_VALUE</Label> or <Label size="small">SELLING_PRICE</Label> Selling price of unit.</List.Item>
                  <List.Item><Label size="small">ROUND(value)</Label> Rounds cents to the nearest ones.</List.Item>
                  <List.Item><Label size="small">CEIL(value)</Label> Rounds cents up to the nearest ones.</List.Item>
                  <List.Item><Label size="small">FLOOR(value)</Label> Rounds cents down to the nearest ones.</List.Item>
                  <List.Item><Label size="small">PERCENT(value, percentage)</Label> Get percent of value.</List.Item>
                  <List.Item><Label size="small">TIER(&#34;Tier Name&#34;)</Label> Get value of another tier.</List.Item>
                </List>
                <b>Examples</b><br />
                <List>
                  <List.Item><Label size="small">SPA Value-3%-7%-2%-RM500</Label> would be <Label color="grey" size="small">(((SPA_VALUE*(1-0.03))*(1-0.07))*(1-0.02))-500</Label></List.Item>
                  <List.Item><Label size="small">RM250,000.00</Label> would be <Label color="grey" size="small">250000.00</Label></List.Item>
                </List>
              </Modal.Content>
              <Modal.Actions>
                <Button onClick={this.handleHelpModalClose}>
                  Close
                </Button>
              </Modal.Actions>
            </Modal>
            <Modal open={deleteModalIsOpen} basic size="small">
              <Header icon="warning sign" content="WARNING: Delete Confirmation" />
              <Modal.Content>
                <h4>Are you sure you want to delete this discount package?{deleteItemName ? ` (${deleteItemName})` : ''}</h4>
                <p>This cannot be undone.</p>
              </Modal.Content>
              <Modal.Actions>
                <Button onClick={this.handleDeleteModalCancel}>
                  <Icon name="remove" /> Cancel
                </Button>
                <Button negative onClick={this.handleDeleteModalConfirm}>
                  <Icon name="trash" /> Delete
                </Button>
              </Modal.Actions>
            </Modal>
            <Segment color="teal">
              <Grid className="head">
                <Grid.Row verticalAlign="middle">
                  <Grid.Column textAlign="center">
                    <h3>Discount Package Settings</h3>
                    <Form.Group widths="equal">
                      <Form.Select
                        fluid
                        label="Default Discount Package"
                        name="defaultProjectPricingID"
                        onChange={onDefaultInputChange}
                        options={this.getDefaultOptions()}
                        placeholder="Disabled"
                        search
                        value={defaultProjectPricingID}
                      />
                      <Form.Select
                        fluid
                        label="Default Bumi Discount Package"
                        name="defaultBumiProjectPricingID"
                        onChange={onDefaultInputChange}
                        options={this.getDefaultOptions()}
                        placeholder="Disabled"
                        search
                        value={defaultBumiProjectPricingID}
                      />
                    </Form.Group>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
            <Divider hidden />
            <SortableList
              addButtonIsDisabled={this.checkAddButtonDisabled}
              helperClass="projectPricingTier isSorting"
              helpModal={this.handleHelpModal}
              items={this.getSortedData(data)}
              lockAxis="y"
              lockToContainerEdges
              onAdd={this.handleOnAdd}
              onChange={this.handleOnChange}
              onDeleteItem={this.handleOnDelete}
              onSortEnd={this.onSortEnd}
              useDragHandle
              onSchedule={this.handleOnSchedule}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ProjectPricingTier.jsx